import type { ReportRemoteExportResult } from '../models/export.types'
import type { ReportResponse } from '../models/report.types'
import { parseReportExportResponseBody } from '../models/reportExportResponse.wire'
import { parseReportResponseBody } from '../models/reportResponse.wire'

import { controllerHttp, decodeDto } from '@/shared/http'

/** One attempt at a heavy controller read; also the worst-case time to the error state. */
export const REPORT_READ_TIMEOUT_MS = 120_000

/**
 * Controller reads opt out of the generic GET retry (ORP-20216). `shared/http` replays a timing-out
 * safe read twice, which turned this 120s budget into ~360s of blank screen before the error state
 * — three times the wait for the same answer. v3 replayed neither call, the feature already renders
 * its own Retry control (`DataVisibleContent.vue`), and the export request is a GET that starts
 * server-side work, so replaying it is not the free read the policy assumes.
 */
function readConfig(signal?: AbortSignal): {
  suppressGlobalErrorHandler: true
  meta: { suppressGlobalErrorHandler: true; timeoutMs: number; retry: false }
  signal?: AbortSignal
} {
  return {
    suppressGlobalErrorHandler: true,
    meta: {
      suppressGlobalErrorHandler: true,
      timeoutMs: REPORT_READ_TIMEOUT_MS,
      retry: false,
    },
    ...(signal ? { signal } : {}),
  }
}

export class ReportApi {
  constructor(private readonly controller: Pick<typeof controllerHttp, 'get'> = controllerHttp) {}

  /** Heavy controller read; feature owns the four-state UX and component-level cancellation. */
  async fetchReport(url: string, query: string, signal?: AbortSignal): Promise<ReportResponse> {
    const response = await this.controller.get<unknown>(`${url}${query}`, readConfig(signal))
    return decodeDto(response.data, parseReportResponseBody)
  }

  /** Export calls have a response union distinct from report rows and therefore a separate parser. */
  async requestExport(
    url: string,
    query: string,
    signal?: AbortSignal,
  ): Promise<ReportRemoteExportResult> {
    const response = await this.controller.get<unknown>(`${url}${query}`, readConfig(signal))
    return decodeDto(response.data, parseReportExportResponseBody)
  }
}

export const reportApi = new ReportApi()
